import { LitElement, html, unsafeCSS } from 'lit';
import { property, customElement, state } from 'lit/decorators.js';
import { MutationController } from '@lit-labs/observers/mutation-controller.js';
import { ComponentLoaderMixin } from '@trp-dms-digital-shared/ds-ui-library-components/src/lib/component-loader/component-loader.js';
import { DigitalSharedAnalytics } from '@trp-dms-digital-shared/ds-ui-library-common/src/utils/analytics/analytics.js';
import {
  getInverseBeaconMode,
  getAlternateBeaconMode,
  kebabOrSnakeCaseToPascalCase,
} from '@trp-dms-digital-shared/ds-ui-library-common';
import mixin from '@trp-dms-digital-shared/ds-ui-library-common/src/lib/mixin.js';

import style from './base.scss?inline';
import { Constants } from './base.constants.js';
import {
  getClosestBeaconMode,
  hasBeaconMode,
} from '../common/utils/closest.js';
import { PopConfiguration } from '../common/configuration/pop-configuration.js';
import { FootnoteIndicatorService } from '../common/utils/footnote-indicator.service.js';
import { i18nService } from '../common/i18n/i18n.js';

type TTrackingHTMLElement = HTMLElement &
  Record<string, (originalEvent: Event) => void>;

/**
 * Base class for components that don't fetch API data
 */
@customElement('pop-base-component')
export class PopBaseComponent extends mixin(LitElement, ComponentLoaderMixin) {
  /**
   * @internal
   */
  static styles = [unsafeCSS(style)];

  /**
   * @internal
   */
  @state()
  tagName = '';

  /**
   * @internal
   */
  protected popConfiguration: PopConfiguration = new PopConfiguration();

  /**
   * @internal
   */
  protected footnoteService!: FootnoteIndicatorService;

  /**
   * Product country
   */
  @property({
    attribute: Constants.DATA_COUNTRY,
    type: String,
  })
  protected country?: string;

  /**
   * Product language
   */
  @property({
    attribute: Constants.DATA_LANGUAGE,
    type: String,
  })
  protected language?: string;

  /**
   * The beacon mode transform to apply to the closest parent beacon mode
   */
  @property({ attribute: 'beacon-mode-transform' })
  beaconModeTransform: 'none' | 'inverse' | 'alternate' = 'none';

  /**
   * The current version of the pop component, used for version specific scoping
   * @internal
   */
  @property({ attribute: Constants.POP_VERSION, reflect: true })
  version = Constants.VERSION;

  /**
   * The optional component object that contains the pre-fetched data to be rendered.
   * @ignore
   */
  @property({
    attribute: Constants.DATA_COMPONENT_OBJECT,
    type: Object,
  })
  componentObject?: unknown;

  enableI18n = false;

  constructor() {
    super();
  }

  connectedCallback() {
    if (this.enableI18n) {
      i18nService.loadi18nForLanguage(this.locale, () => {
        this.requestUpdate();
      });
    }
    this.footnoteService = new FootnoteIndicatorService();

    const mutationController = new MutationController(this, {
      config: { attributes: true },
      callback: () => {
        this.footnoteService.applyFootnoteIndicatorsToAll();
      },
    });
    mutationController?.observe(this);
    super.connectedCallback();
  }

  /**
   * If the component is disconnected from the DOM, remove the analytics event listeners.
   */
  disconnectedCallback() {
    DigitalSharedAnalytics.unsetHostEventHandlerForAnalytics(
      this as unknown as TTrackingHTMLElement,
    );
    super.disconnectedCallback();
  }

  /*
   * This method is called from the Component Loader Mixin once any Defer conditions are met.
   * No need to override this method when extending the Base Component.
   * Wrapper to be used inside of the `render()` method
   * @example
   * render() {
   *  return this.renderComponent(html`<p>meow</p>`)
   * }
   */
  protected renderComponent(
    content: unknown,
    classes = 'beacon-theme-modern-outlook',
  ) {
    return !this.initialized
      ? html`<!--- Not Initialized --->`
      : html`
          <div data-base-container class="b-reset ${classes}">${content}</div>
        `;
  }

  /**
   * Checks for parents to reflect the closest beacon mode onto the container
   */
  updated() {
    if (this.enableI18n) {
      i18nService.loadi18nForLanguage(this.locale, () => {
        this.requestUpdate();
      });
    }
    DigitalSharedAnalytics.unsetHostEventHandlerForAnalytics(
      this as unknown as TTrackingHTMLElement,
    );

    DigitalSharedAnalytics.setHostEventHandlerForAnalytics(
      this as unknown as TTrackingHTMLElement,
      Constants.POP_VERSION,
      this.version,
    );

    const container = this.shadowRoot?.querySelector(
      'div[data-base-container]',
    );

    // If the container doesn't have the beacon mode added yet, then add it
    if (!hasBeaconMode(this)) {
      const beaconParent = getClosestBeaconMode(this);
      if (!beaconParent) {
        return;
      }
      let beaconMode = beaconParent;
      if (this.beaconModeTransform === 'inverse') {
        beaconMode = getInverseBeaconMode(beaconParent);
      } else if (this.beaconModeTransform === 'alternate') {
        beaconMode = getAlternateBeaconMode(beaconParent);
      }
      container?.classList.add(beaconMode);
    }
  }

  render() {
    return this.renderComponent(html`<!--- Base Render Content --->`);
  }

  /**
   * Checks if the i18n resources are loaded for the current locale.
   * @returns {boolean} True if the i18n resources are loaded, false otherwise.
   */
  get isI18nLoaded() {
    const lng = this.locale;
    return i18nService.isI18nLoadedForLanguage(lng);
  }

  /**
   * Constructs the locale string from the language and country properties.
   * @returns {string} The locale string in the format 'language-country'.
   */
  get locale() {
    return `${this.language?.toLowerCase()}-${this.country?.toUpperCase()}`;
  }

  /**
   * Returns a localization function for the current locale.
   * Note: The `enableI18n` flag should be set to true before calling this method.
   */
  get localize() {
    if (!this.enableI18n) {
      console.warn('I18N is not enabled. Returning a placeholder function.');
      return () => 'I18N NOT ENABLED';
    }

    const lng = this.locale;

    // getFixedT will not automatically load the translations for the specified language
    // if they are not present so we have to load it ourselves. Also due to some issues
    // with initialisation order we call this method to ensure i18next is initialised correctly as well.
    i18nService.loadi18nForLanguage(lng, () => {
      // As the language gets loaded asynchonously we'll now have to force an update
      // since the strings will have already been rendered without the correct language loaded.
      this.requestUpdate();
    });

    if (i18nService.isI18nSetup()) {
      return i18nService.getFixedT(lng, null, null);
    } else {
      console.warn('i18n not ready showing empty string');
      return () => '';
    }
  }

  /**
   * Defines this web component in the CustomElementRegistry.
   */
  static registerComponent(this: any) {
    if (this.tagName) {
      if (!customElements.get(this.tagName)) {
        const ComponentClass: any = class extends this {};
        Object.defineProperty(ComponentClass, 'name', {
          value: kebabOrSnakeCaseToPascalCase(this.tagName),
        });
        customElements.define(this.tagName, ComponentClass);
        customElements
          .whenDefined(this.tagName)
          .then(() => {
            document.dispatchEvent(
              new CustomEvent(`pop:product:${this.tagName}:registered`, {
                detail: this.tagName,
              }),
            );
          })
          .catch((e) => {
            console.error(
              `Error defining POP Component ${this.tagName} in CustomElementsRegistry: `,
              e,
            );
          });
      }
    }
  }
}
